import {
  ALARM_TYPES,
  GEOFENCE_KINDS,
  VEHICLE_TYPES,
  openRing,
  type Geofence,
  type GeofenceKind,
  type LonLat,
  ALERT_TIME_ZONE,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_KINDS,
  NOTIFICATION_STATUSES,
  toHm,
  type Alarm,
  type AlarmNotification,
  type AlarmType,
  type NotificationChannel,
  type NotificationKind,
  type NotificationStatus,
  type SiteAlertSettings,
  type SiteNightContact,
} from "@fts/core";
import type {
  CardStatus,
  CardTech,
  Device,
  DocumentKind,
  Invitation,
  InvitationPreview,
  InvitationStatus,
  LiveSnapshot,
  Membership,
  Operator,
  OperatorCard,
  OperatorDocument,
  OperatorPermission,
  PowerType,
  RegisteredDevice,
  Site,
  SiteKind,
  Tenant,
  TenantRole,
  Vehicle,
  VehicleType,
} from "@fts/core";
import type {
  AlertPerson,
  AlertSettingsInput,
  ContactCandidate,
  EffectiveAlertSettings,
  EffectiveNightContacts,
  AlertRecipientsStatus,
  PersonName,
  GeofenceInput,
  AuthlistStatus, Member, OperatorInput, SiteInput, VehicleInput } from "./provider";

/** Row shapes returned by PostgREST for the tables in supabase/migrations. */
export type TenantRow = { id: string; name: string; slug: string; created_at: string };
export type SiteRow = {
  id: string;
  tenant_id: string;
  parent_id: string | null;
  name: string;
  kind: SiteKind;
  address: string | null;
  lat: number | null;
  lon: number | null;
};
export type VehicleRow = {
  id: string;
  tenant_id: string;
  site_id: string;
  fleet_no: string;
  type: VehicleType;
  power: PowerType;
  make: string | null;
  model: string | null;
  serial_no: string | null;
  battery_voltage: number | null;
  active: boolean;
  unauthorized_start_alarm: boolean;
  /** One-to-one embed through devices.vehicle_id. PostgREST returns an object or null (or an array on older versions). */
  devices: { device_id: string } | { device_id: string }[] | null;
};
export type MembershipRow = { tenant_id: string; user_id: string; role: TenantRole };
export type MembershipSiteRow = { tenant_id: string; user_id: string; site_id: string };
export type ProfileRow = { user_id: string; email: string; display_name: string };

export const toTenant = (r: TenantRow): Tenant => ({ id: r.id, name: r.name, slug: r.slug, createdAt: r.created_at });

export const toSite = (r: SiteRow): Site => ({
  id: r.id,
  tenantId: r.tenant_id,
  parentId: r.parent_id,
  name: r.name,
  kind: r.kind,
  address: r.address,
  lat: r.lat,
  lon: r.lon,
});

export function toVehicle(r: VehicleRow): Vehicle {
  const dev = Array.isArray(r.devices) ? r.devices[0] : r.devices;
  return {
    id: r.id,
    tenantId: r.tenant_id,
    siteId: r.site_id,
    fleetNo: r.fleet_no,
    type: r.type,
    power: r.power,
    make: r.make,
    model: r.model,
    serialNo: r.serial_no,
    batteryVoltage: r.battery_voltage,
    active: r.active,
    // Missing (older row shape) = the database default: on.
    unauthorizedStartAlarm: r.unauthorized_start_alarm !== false,
    deviceId: dev?.device_id ?? null,
  };
}

export function toMemberships(rows: MembershipRow[], scopes: MembershipSiteRow[]): Membership[] {
  return rows.map((m) => ({
    tenantId: m.tenant_id,
    userId: m.user_id,
    role: m.role,
    siteIds: scopes.filter((s) => s.tenant_id === m.tenant_id && s.user_id === m.user_id).map((s) => s.site_id),
  }));
}

export function toMembers(rows: MembershipRow[], scopes: MembershipSiteRow[], profiles: ProfileRow[]): Member[] {
  const byId = new Map(profiles.map((p) => [p.user_id, p]));
  return toMemberships(rows, scopes).map((m) => {
    const p = byId.get(m.userId);
    return { userId: m.userId, email: p?.email ?? "", displayName: p?.display_name ?? p?.email ?? m.userId, role: m.role, siteIds: m.siteIds };
  });
}

/* ---------------- Sprint 2: devices and invitations ---------------- */

export const DEVICE_COLUMNS = "device_id, tenant_id, vehicle_id, hw, fw, imei, iccid, online, last_seen_at, claimed_at, revoked_at, has_card_reader";
export type DeviceRow = {
  device_id: string;
  tenant_id: string | null;
  vehicle_id: string | null;
  hw: string | null;
  fw: string | null;
  imei: string | null;
  iccid: string | null;
  online: boolean | null;
  last_seen_at: string | null;
  claimed_at: string | null;
  revoked_at: string | null;
  /** ADR 0023; absent in rows read before the column existed. */
  has_card_reader?: boolean | null;
};

export const toDevice = (r: DeviceRow): Device => ({
  deviceId: r.device_id,
  tenantId: r.tenant_id,
  vehicleId: r.vehicle_id,
  hw: r.hw,
  fw: r.fw,
  imei: r.imei,
  iccid: r.iccid,
  online: r.revoked_at ? false : !!r.online,
  lastSeenAt: r.last_seen_at,
  claimedAt: r.claimed_at,
  revoked: r.revoked_at !== null,
  hasCardReader: r.has_card_reader !== false,
});

export type RegisteredDeviceRow = { device_id: string; secret: string; claim_code: string };
export const toRegisteredDevice = (r: RegisteredDeviceRow): RegisteredDevice => ({ deviceId: r.device_id, secret: r.secret, claimCode: r.claim_code });

/** Only the columns granted to `authenticated` (token_hash is not readable). */
export const INVITATION_COLUMNS = "id, tenant_id, email, role, site_ids, created_at, expires_at, accepted_at, revoked_at";
export type InvitationRow = {
  id: string;
  tenant_id: string;
  email: string;
  role: TenantRole;
  site_ids: string[] | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
};

/** Same order as public.invitation_status(). */
export function invitationStatus(r: Pick<InvitationRow, "accepted_at" | "revoked_at" | "expires_at">, now = Date.now()): InvitationStatus {
  if (r.accepted_at) return "accepted";
  if (r.revoked_at) return "revoked";
  if (Date.parse(r.expires_at) < now) return "expired";
  return "pending";
}

export const toInvitation = (r: InvitationRow, now = Date.now()): Invitation => ({
  id: r.id,
  tenantId: r.tenant_id,
  email: r.email,
  role: r.role,
  siteIds: r.site_ids ?? [],
  createdAt: r.created_at,
  expiresAt: r.expires_at,
  status: invitationStatus(r, now),
});

export type InvitationPreviewRow = { tenant_name: string; role: TenantRole; status: InvitationStatus; expires_at: string };
export const toInvitationPreview = (r: InvitationPreviewRow): InvitationPreview => ({
  tenantName: r.tenant_name,
  role: r.role,
  status: r.status,
  expiresAt: r.expires_at,
});

/** Writable site columns (tenant_id only on insert: it is immutable). */
export function siteToRow(input: SiteInput): Omit<SiteRow, "id" | "tenant_id"> {
  return {
    parent_id: input.parentId,
    name: input.name.trim(),
    kind: input.kind,
    address: input.address?.trim() || null,
    lat: input.lat,
    lon: input.lon,
  };
}

export function vehicleToRow(input: VehicleInput): Omit<VehicleRow, "id" | "tenant_id" | "devices"> {
  return {
    site_id: input.siteId,
    fleet_no: input.fleetNo.trim(),
    type: input.type,
    power: input.power,
    make: input.make?.trim() || null,
    model: input.model?.trim() || null,
    serial_no: input.serialNo?.trim() || null,
    battery_voltage: input.batteryVoltage,
    active: input.active,
    unauthorized_start_alarm: input.unauthorizedStartAlarm,
  };
}

/* ---- Sprint 4: live map ---- */

export const LIVE_COLUMNS =
  "device_id, vehicle_id, ts, received_at, lat, lon, pos_at, pos_acc, pos_src, speed_kmh, ignition, seat, belt, operator_uid, link, rssi, supply_v, last_event_type, last_event_at, last_alarm_type, last_alarm_at, soc, bat_v, bat_a, bat_t, bat_at";
export type LiveRow = {
  device_id: string;
  vehicle_id: string | null;
  ts: string;
  received_at: string | null;
  lat: number | null;
  lon: number | null;
  pos_at?: string | null;
  pos_acc: number | null;
  pos_src: string | null;
  speed_kmh: number | null;
  ignition: boolean | null;
  seat: boolean | null;
  belt: boolean | null;
  operator_uid: string | null;
  link: string | null;
  rssi: number | null;
  supply_v: number | null;
  last_event_type: string | null;
  last_event_at: string | null;
  last_alarm_type?: string | null;
  last_alarm_at?: string | null;
  /* Sprint 7a: battery module (ICD v0.2 section 9). */
  soc?: number | null;
  bat_v?: number | null;
  bat_a?: number | null;
  bat_t?: number | null;
  bat_at?: string | null;
};

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
const bool = (v: unknown): boolean | null => (typeof v === "boolean" ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

/** Also used for Realtime payloads, which carry the raw row (numbers may arrive as strings). */
export const toLive = (r: LiveRow): LiveSnapshot => ({
  deviceId: r.device_id,
  vehicleId: str(r.vehicle_id),
  ts: r.ts,
  receivedAt: str(r.received_at),
  lat: num(r.lat),
  lon: num(r.lon),
  posAt: str(r.pos_at),
  posAcc: num(r.pos_acc),
  posSrc: str(r.pos_src),
  speedKmh: num(r.speed_kmh),
  ignition: bool(r.ignition),
  seat: bool(r.seat),
  belt: bool(r.belt),
  operatorUid: str(r.operator_uid),
  link: str(r.link),
  rssi: num(r.rssi),
  supplyV: num(r.supply_v),
  lastEventType: str(r.last_event_type),
  lastEventAt: str(r.last_event_at),
  lastAlarmType: str(r.last_alarm_type),
  lastAlarmAt: str(r.last_alarm_at),
  soc: num(r.soc),
  batV: num(r.bat_v),
  batA: num(r.bat_a),
  batT: num(r.bat_t),
  batAt: str(r.bat_at),
});

/* ---------------- Sprint 5: operators ---------------- */

export const OPERATOR_COLUMNS = "id, tenant_id, site_id, full_name, employee_no, active, left_on, anonymized_at";
export type OperatorRow = {
  id: string;
  tenant_id: string;
  site_id: string | null;
  full_name: string;
  employee_no: string | null;
  active: boolean;
  left_on: string | null;
  anonymized_at: string | null;
};
export const toOperator = (r: OperatorRow): Operator => ({
  id: r.id,
  tenantId: r.tenant_id,
  siteId: r.site_id,
  fullName: r.full_name,
  employeeNo: r.employee_no,
  active: r.active,
  leftOn: r.left_on,
  anonymized: r.anonymized_at !== null,
});
export const operatorToRow = (i: OperatorInput) => ({
  site_id: i.siteId,
  full_name: i.fullName.trim(),
  employee_no: i.employeeNo?.trim() || null,
  active: i.active,
  left_on: i.leftOn,
});

export const CARD_COLUMNS = "id, tenant_id, operator_id, uid, tech, status, created_at";
export type CardRow = { id: string; tenant_id: string; operator_id: string; uid: string; tech: CardTech; status: CardStatus; created_at: string };
export const toCard = (r: CardRow): OperatorCard => ({
  id: r.id,
  tenantId: r.tenant_id,
  operatorId: r.operator_id,
  uid: r.uid,
  tech: r.tech,
  status: r.status,
  createdAt: r.created_at,
});

export const DOCUMENT_COLUMNS = "id, tenant_id, operator_id, kind, label, valid_until, required";
export type DocumentRow = { id: string; tenant_id: string; operator_id: string; kind: DocumentKind; label: string | null; valid_until: string; required: boolean };
export const toDocument = (r: DocumentRow): OperatorDocument => ({
  id: r.id,
  tenantId: r.tenant_id,
  operatorId: r.operator_id,
  kind: r.kind,
  label: r.label,
  validUntil: r.valid_until,
  required: r.required,
});

export const PERMISSION_COLUMNS = "id, tenant_id, operator_id, site_id, vehicle_type, vehicle_id, valid_until";
export type PermissionRow = {
  id: string;
  tenant_id: string;
  operator_id: string;
  site_id: string | null;
  vehicle_type: VehicleType | null;
  vehicle_id: string | null;
  valid_until: string | null;
};
export const toPermission = (r: PermissionRow): OperatorPermission => ({
  id: r.id,
  tenantId: r.tenant_id,
  operatorId: r.operator_id,
  siteId: r.site_id,
  vehicleType: r.vehicle_type,
  vehicleId: r.vehicle_id,
  validUntil: r.valid_until,
});

/** Only these columns are granted to clients (entries hold card UIDs and stay server-side). */
export const AUTHLIST_COLUMNS = "device_id, ver, changed_at, sent_ver, sent_at, acked_ver, acked_at, error";
export type AuthlistRow = {
  device_id: string;
  ver: number;
  changed_at: string | null;
  sent_ver: number | null;
  sent_at: string | null;
  acked_ver: number | null;
  acked_at: string | null;
  error: string | null;
};
export const toAuthlistStatus = (r: AuthlistRow): AuthlistStatus => ({
  deviceId: r.device_id,
  ver: r.ver,
  changedAt: r.changed_at,
  sentVer: r.sent_ver,
  sentAt: r.sent_at,
  ackedVer: r.acked_ver,
  ackedAt: r.acked_at,
  error: r.error,
});

/* ---------------- Sprint 6a: alarms (ADR 0011) ---------------- */

/** Granted alarm columns. operator_uid (card number) is service-only and must never be selected here. */
export const ALARM_COLUMNS =
  "id, tenant_id, site_id, vehicle_id, device_id, event_id, type, severity, occurred_at, received_at, operator_id, off_hours, delayed, time_unknown, repeat_count, last_occurred_at, notify_error, status, acked_by, acked_at, ack_note, created_at, geofence_id, lat, lon, checklist_submission_id, shift_name, shift_start";
export type AlarmRow = {
  id: number | string;
  tenant_id: string;
  site_id: string | null;
  vehicle_id: string | null;
  device_id: string | null;
  event_id: number | string | null;
  type: string;
  severity: number | string | null;
  occurred_at: string;
  received_at: string | null;
  operator_id: string | null;
  off_hours: boolean | null;
  delayed: boolean | null;
  time_unknown?: boolean | null;
  repeat_count?: number | string | null;
  last_occurred_at?: string | null;
  notify_error?: string | null;
  status: string;
  acked_by: string | null;
  acked_at: string | null;
  ack_note: string | null;
  created_at: string;
  /** Sprint 6b (ADR 0015): zone and violation point of geofence alarms. */
  geofence_id?: string | null;
  lat?: number | string | null;
  lon?: number | string | null;
  /** Sprint 7a (ADR 0016): checklist alarms. */
  checklist_submission_id?: string | null;
  shift_name?: string | null;
  shift_start?: string | null;
};

const idStr = (v: number | string | null): string | null => (v === null || v === undefined ? null : String(v));

/** Unknown alarm types (a newer database) are shown as tamper-like "other" by the UI; keep the raw value. */
export function toAlarm(r: AlarmRow): Alarm {
  const severity = num(r.severity);
  return {
    id: String(r.id),
    tenantId: r.tenant_id,
    siteId: r.site_id,
    vehicleId: r.vehicle_id,
    deviceId: r.device_id,
    eventId: idStr(r.event_id),
    type: r.type as AlarmType,
    severity: severity !== null && severity >= 1 && severity <= 3 ? severity : 2,
    occurredAt: r.occurred_at,
    receivedAt: r.received_at,
    operatorId: r.operator_id,
    offHours: r.off_hours === true,
    delayed: r.delayed === true || r.time_unknown === true,
    timeUnknown: r.time_unknown === true,
    repeatCount: Math.max(0, num(r.repeat_count ?? null) ?? 0),
    lastOccurredAt: r.last_occurred_at ?? null,
    notifyError: r.notify_error ?? null,
    status: r.status === "acknowledged" ? "acknowledged" : "open",
    ackedBy: r.acked_by,
    ackedAt: r.acked_at,
    ackNote: r.ack_note,
    createdAt: r.created_at,
    geofenceId: r.geofence_id ?? null,
    lat: num(r.lat ?? null),
    lon: num(r.lon ?? null),
    // Only present on checklist alarms (undefined keeps other alarms unchanged).
    checklistSubmissionId: r.checklist_submission_id ?? undefined,
    shiftName: r.shift_name ?? undefined,
    shiftStart: r.shift_start ?? undefined,
  };
}

export const isKnownAlarmType = (t: string): t is AlarmType => (ALARM_TYPES as readonly string[]).includes(t);

/** Columns the console needs; error text and retry bookkeeping are not shown. */
export const NOTIFICATION_COLUMNS = "id, tenant_id, alarm_id, user_id, external_recipient_id, channel, kind, status, due_at, sent_at";
export type NotificationRow = {
  id: number | string;
  tenant_id: string;
  alarm_id: number | string | null;
  /** null for a maintenance report to an external address (ADR 0016). */
  user_id: string | null;
  external_recipient_id?: string | null;
  channel: string;
  kind: string;
  status: string;
  due_at: string | null;
  sent_at: string | null;
};

const pick = <T extends string>(list: readonly T[], v: string, fallback: T): T => ((list as readonly string[]).includes(v) ? (v as T) : fallback);

export const toNotification = (r: NotificationRow): AlarmNotification => ({
  id: String(r.id),
  tenantId: r.tenant_id,
  alarmId: idStr(r.alarm_id),
  userId: r.user_id,
  ...(r.external_recipient_id ? { externalRecipientId: r.external_recipient_id } : {}),
  channel: pick<NotificationChannel>(NOTIFICATION_CHANNELS, r.channel, "email"),
  kind: pick<NotificationKind>(NOTIFICATION_KINDS, r.kind, "immediate"),
  status: pick<NotificationStatus>(NOTIFICATION_STATUSES, r.status, "pending"),
  dueAt: r.due_at,
  sentAt: r.sent_at,
});

export const ALERT_SETTINGS_COLUMNS = "tenant_id, site_id, work_days, work_start, work_end, time_zone";
export type AlertSettingsRow = {
  tenant_id: string;
  site_id: string;
  work_days: Array<number | string> | null;
  work_start: string;
  work_end: string;
  time_zone: string | null;
};

const toDays = (v: Array<number | string> | null): number[] =>
  [...new Set((v ?? []).map(Number).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7))].sort((a, b) => a - b);

export const toAlertSettings = (r: AlertSettingsRow): SiteAlertSettings => ({
  tenantId: r.tenant_id,
  siteId: r.site_id,
  workDays: toDays(r.work_days),
  workStart: toHm(r.work_start),
  workEnd: toHm(r.work_end),
  tz: r.time_zone || ALERT_TIME_ZONE,
});

/**
 * Columns an UPDATE may set (site_id/tenant_id are not granted for update). The insert adds
 * site_id and tenant_id; the database trigger fills tenant_id from the site anyway.
 */
export const alertSettingsToRow = (i: AlertSettingsInput): Pick<AlertSettingsRow, "work_days" | "work_start" | "work_end" | "time_zone"> => ({
  work_days: [...new Set(i.workDays)].sort((a, b) => a - b),
  work_start: i.workStart,
  work_end: i.workEnd,
  time_zone: ALERT_TIME_ZONE,
});

/** Row of effective_alert_settings(site). */
export type EffectiveSettingsRow = {
  work_days: Array<number | string> | null;
  work_start: string;
  work_end: string;
  time_zone: string | null;
  source_site_id: string | null;
};

export const toEffectiveSettings = (r: EffectiveSettingsRow): EffectiveAlertSettings => ({
  hours: { workDays: toDays(r.work_days), workStart: toHm(r.work_start), workEnd: toHm(r.work_end), tz: r.time_zone || ALERT_TIME_ZONE },
  sourceSiteId: r.source_site_id,
});

/** Row of alert_contact_candidates(site). */
export type ContactCandidateRow = { user_id: string; display_name: string | null; role: TenantRole };
export const toContactCandidate = (r: ContactCandidateRow): ContactCandidate => ({ userId: r.user_id, displayName: r.display_name || "—", role: r.role });

/** Row of alarm_people(alarm). */
export type PersonNameRow = { user_id: string; display_name: string | null };
export const toPersonName = (r: PersonNameRow): PersonName => ({ userId: r.user_id, displayName: r.display_name || "—" });

/** Rows of effective_night_contacts(site): every row carries the same source_site_id. */
export type EffectiveNightContactRow = PersonNameRow & { source_site_id: string | null };
export const toEffectiveNightContacts = (rows: EffectiveNightContactRow[]): EffectiveNightContacts => ({
  contacts: rows.map(toPersonName),
  sourceSiteId: rows[0]?.source_site_id ?? null,
});

/** Row of site_alert_recipients_status(site): counts only. */
export type AlertRecipientsStatusRow = {
  day_recipients: number | null;
  night_recipients: number | null;
  admins_fallback: boolean | null;
  admin_copies?: number | null;
};
export const toAlertRecipientsStatus = (rows: AlertRecipientsStatusRow[]): AlertRecipientsStatus | null => {
  const r = rows[0];
  if (!r) return null;
  return {
    dayRecipients: Number(r.day_recipients ?? 0),
    nightRecipients: Number(r.night_recipients ?? 0),
    adminsFallback: r.admins_fallback === true,
    adminCopies: Number(r.admin_copies ?? 0),
  };
};

export const NIGHT_CONTACT_COLUMNS = "tenant_id, site_id, user_id";
export type NightContactRow = { tenant_id: string; site_id: string; user_id: string };
export const toNightContact = (r: NightContactRow): SiteNightContact => ({ tenantId: r.tenant_id, siteId: r.site_id, userId: r.user_id });

export const toAlertPeople = (members: Member[]): AlertPerson[] =>
  members.map((m) => ({ userId: m.userId, displayName: m.displayName, role: m.role, siteIds: m.siteIds }));

/* ---------------- Sprint 6b: geofences (ADR 0015) ---------------- */

export const GEOFENCE_COLUMNS =
  "id, tenant_id, site_id, name, kind, shape, coords, center_lat, center_lon, radius_m, vehicle_types, dwell_limit_s, active, created_by, created_at, updated_at";
export type GeofenceRow = {
  id: string;
  tenant_id: string;
  site_id: string;
  name: string;
  kind: string;
  shape: string;
  /** jsonb [[lon, lat], …] for polygons, null for circles. */
  coords: unknown;
  center_lat: number | string | null;
  center_lon: number | string | null;
  radius_m: number | string | null;
  vehicle_types: string[] | null;
  dwell_limit_s: number | string | null;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

/** jsonb coordinates -> [[lon, lat], …]; malformed pairs are dropped (the zone then shows as incomplete). */
export function toCoords(v: unknown): LonLat[] | null {
  if (!Array.isArray(v)) return null;
  const out: LonLat[] = [];
  for (const p of v) {
    if (!Array.isArray(p) || p.length < 2) continue;
    const lon = num(p[0]);
    const lat = num(p[1]);
    if (lon !== null && lat !== null) out.push([lon, lat]);
  }
  return out;
}

export const toGeofence = (r: GeofenceRow): Geofence => ({
  id: r.id,
  tenantId: r.tenant_id,
  siteId: r.site_id,
  name: r.name,
  kind: pick<GeofenceKind>(GEOFENCE_KINDS, r.kind, "forbidden"),
  shape: r.shape === "circle" ? "circle" : "polygon",
  coords: r.shape === "circle" ? null : toCoords(r.coords),
  centerLat: num(r.center_lat),
  centerLon: num(r.center_lon),
  radiusM: num(r.radius_m),
  vehicleTypes: (r.vehicle_types ?? []).filter((t): t is VehicleType => (VEHICLE_TYPES as readonly string[]).includes(t)),
  dwellLimitS: num(r.dwell_limit_s),
  active: r.active !== false,
  createdBy: r.created_by,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

/**
 * Columns written on insert and update. Fields of the other shape and the dwell limit of non-dwell
 * zones are null; a polygon is stored without a closing vertex. tenant_id/site_id are added on insert
 * only; created_by, created_at and updated_at are set by the database.
 */
export function geofenceToRow(i: GeofenceInput): Omit<GeofenceRow, "id" | "tenant_id" | "site_id" | "created_by" | "created_at" | "updated_at"> {
  const circle = i.shape === "circle";
  return {
    name: i.name.trim(),
    kind: i.kind,
    shape: i.shape,
    coords: circle ? null : openRing(i.coords ?? []),
    center_lat: circle ? i.centerLat : null,
    center_lon: circle ? i.centerLon : null,
    radius_m: circle ? i.radiusM : null,
    vehicle_types: [...new Set(i.vehicleTypes)],
    dwell_limit_s: i.kind === "dwell" ? i.dwellLimitS : null,
    active: i.active,
  };
}
